import React, { useContext } from 'react';
import styles from './SFNavContent.module.scss';
import { SFIconButton, SFPaper, SFScrollable } from 'sfui';
import SFNavContentLink from './SFNavContentLink/SFNavContentLink';
import { SFNavLink } from '../SFNavLink';
import { OnlineStatusContext } from '../../../Context/OnlineStatusContext';
import { useLocation } from 'react-router-dom';
import { ReminderProps, SFLogo } from 'ui-smartforce-settings';
import { SFNavContentBrand } from './SFNavContentBrand/SFNavContentBrand';
import ContentLinkWithReminder from './ContentLinkWithReminder/ContentLinkWithReminder';

export interface SFNavContentProps {
  isPanel: boolean;
  links: SFNavLink[];
  bottomLinks: SFNavLink[];
  isCollapsed?: boolean;
  reminderProps: ReminderProps;
  onToggleCollapse?: () => void;
  onDrawerClose?: () => void;
}

const SFNavContent = ({
  isPanel,
  links,
  bottomLinks,
  isCollapsed = false,
  reminderProps,
  onToggleCollapse,
  onDrawerClose
}: SFNavContentProps): React.ReactElement<SFNavContentProps> => {
  const { isOnline } = useContext(OnlineStatusContext);
  const location = useLocation();

  return (
    <SFPaper
      className={`${styles.sfNavContent} ${
        isCollapsed ? styles.isCollapsed : ''
      }`}
    >
      {!isPanel && (
        <div
          className={`${styles.logo} ${
            isCollapsed ? styles.logoCollapsed : ''
          }`}
        >
          {!isCollapsed && <SFLogo />}
          <SFIconButton
            sfIcon={isCollapsed ? 'Menu-Expanded' : 'Menu-Collapsed'}
            sfSize="medium"
            onClick={onToggleCollapse}
          />
        </div>
      )}

      <div className={styles.content}>
        <SFScrollable
          className={styles.scrollable}
          containerClassName={styles.scrollableContainer}
        >
          <ul>
            {links
              .filter(
                (link: SFNavLink) =>
                  !link.temporal || location.pathname === link.pathname
              )
              .map((link: SFNavLink) => (
                <li key={`link-${link.label}`}>
                  <SFNavContentLink
                    isPanel={isPanel}
                    isCollapsed={isCollapsed}
                    link={link}
                    selected={location.pathname.includes(
                      link.pathname as string
                    )}
                    disabled={!isOnline && !link.offline}
                    onDrawerClose={onDrawerClose}
                  />
                </li>
              ))}
          </ul>
          <div className={styles.settings}>
            <ul>
              {bottomLinks.map((link: SFNavLink) => {
                if (link.label === 'Feature Tours') {
                  return (
                    <li key={`link-${link.label}`}>
                      <ContentLinkWithReminder
                        isCollapsed={isCollapsed}
                        disabled={!isOnline}
                        link={link}
                        selected={false}
                        onDrawerClose={onDrawerClose}
                        tooltipProps={{
                          open:
                            reminderProps.type === 'tours' &&
                            reminderProps.open,
                          type: 'tours',
                          onGotIt: reminderProps.onGotIt
                        }}
                      />
                    </li>
                  );
                } else if (link.label === 'Feedback') {
                  return (
                    <li key={`link-${link.label}`}>
                      <ContentLinkWithReminder
                        isCollapsed={isCollapsed}
                        disabled={!isOnline}
                        link={link}
                        selected={false}
                        onDrawerClose={onDrawerClose}
                        tooltipProps={{
                          open:
                            reminderProps.type === 'surveys' &&
                            reminderProps.open,
                          type: 'surveys',
                          onGotIt: reminderProps.onGotIt
                        }}
                      />
                    </li>
                  );
                } else {
                  return (
                    <li key={`link-${link.label}`}>
                      <SFNavContentLink
                        isCollapsed={isCollapsed}
                        disabled={!isOnline && !link.offline}
                        link={link}
                        selected={link.pathname === location.pathname}
                        onDrawerClose={onDrawerClose}
                      />
                    </li>
                  );
                }
              })}
            </ul>

            <SFNavContentBrand isOnline={isOnline} isCollapsed={isCollapsed} />
          </div>
        </SFScrollable>
      </div>
    </SFPaper>
  );
};

export default SFNavContent;
